import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import { CreditCard } from 'lucide-react';
import RiskCardItem from '../components/RiskCardItem';
import RiskCardModal from '../components/RiskCardModal';
import ConfirmDialog from '../components/ConfirmDialog';

const RiskClassificationCardsPage = () => {
    const [cardsList, setCardsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, cardId: null });

    const LOAN_TYPE = 'risk_classification_cards';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/key-benefits/${LOAN_TYPE}`);
            if (response.data.success) {
                setCardsList(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching cards:', error);
            toast.error('Failed to load risk cards');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingCard) {
                await axios.put(`/api/admin/key-benefits/${editingCard.id}`, formData);
                toast.success('Card updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', formData);
                toast.success('Card created successfully');
            }
            setIsModalOpen(false);
            setEditingCard(null);
            fetchData();
        } catch (error) {
            console.error('Error saving card:', error);
            toast.error('Failed to save card');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, cardId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.cardId}`);
            toast.success('Card deleted successfully');
            setDeleteConfirm({ isOpen: false, cardId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting card:', error);
            toast.error('Failed to delete card');
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Risk Summary Cards</h1>
                    <p className="text-slate-500 mt-1">Manage the summary cards for the Risk Classification page</p>
                </div>
                <button
                    onClick={() => {
                        setEditingCard(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Card
                </button>
            </div>

            {/* Cards Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white rounded-2xl h-48 shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            ) : cardsList.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <CreditCard className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No cards added yet</h3>
                        <p className="text-slate-500 mb-6">Create summary cards to showcase risk metrics.</p>
                        <button
                            onClick={() => {
                                setEditingCard(null);
                                setIsModalOpen(true);
                            }}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                        >
                            Add First Card
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {cardsList.map((card) => (
                        <RiskCardItem
                            key={card.id}
                            card={card}
                            onEdit={(c) => {
                                setEditingCard(c);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <RiskCardModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingCard(null);
                }}
                onSubmit={handleSubmit}
                card={editingCard}
                loading={submitting}
                loanType={LOAN_TYPE}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, cardId: null })}
                onConfirm={confirmDelete}
                title="Delete Card"
                message="Are you sure you want to delete this card? This action cannot be undone."
            />
        </div>
    );
};

export default RiskClassificationCardsPage;
